import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { posicaoTooltip, type PosicaoBalao } from '../lib/posicaoTooltip';

/**
 * Tooltip de orientação: um "?" discreto que abre um balão explicando o elemento
 * ao lado. Abre em hover/foco (desktop) e em toque (mobile); fecha em Esc, toque
 * fora e ao tirar o foco.
 *
 * O balão é renderizado num PORTAL no <body>, com posição FIXED calculada a
 * partir do "?" — assim NUNCA é cortado pelo overflow do sidebar (o bug antigo:
 * com placement CSS `left-full`, o balão abria além da borda direita do <aside>
 * e o `overflow-y-auto` do sidebar o clipava). Se não couber à direita, vira
 * para a esquerda; reposiciona em scroll/resize.
 */
export function Tooltip({
  text,
  placement = 'right',
  label = 'Ajuda',
  className,
}: {
  text: string;
  placement?: 'right' | 'left';
  label?: string;
  className?: string;
}) {
  const id = useId();
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState<PosicaoBalao | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);
  const balaoRef = useRef<HTMLDivElement>(null);

  function recalcular() {
    const g = botaoRef.current?.getBoundingClientRect();
    const b = balaoRef.current?.getBoundingClientRect();
    if (!g) return;
    setPos(
      posicaoTooltip(
        g,
        { width: b?.width ?? 240, height: b?.height ?? 40 },
        { width: window.innerWidth, height: window.innerHeight },
        placement,
      ),
    );
  }

  // Recalcula assim que abre (mede o balão já renderizado) e em scroll/resize.
  useLayoutEffect(() => {
    if (!aberto) return;
    recalcular();
    const onMove = () => recalcular();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setAberto(false);
    }
    function onFora(e: Event) {
      const alvo = e.target as Node;
      if (wrapRef.current?.contains(alvo) || balaoRef.current?.contains(alvo)) return;
      setAberto(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onFora);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onFora);
    };
  }, [aberto]);

  return (
    <span ref={wrapRef} className={`relative inline-flex align-middle ${className ?? ''}`}>
      <button
        ref={botaoRef}
        type="button"
        aria-label={label}
        aria-describedby={aberto ? id : undefined}
        aria-expanded={aberto}
        onMouseEnter={() => setAberto(true)}
        onMouseLeave={() => setAberto(false)}
        onFocus={() => setAberto(true)}
        onBlur={() => setAberto(false)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setAberto((v) => !v);
        }}
        className="flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full border border-line-2 text-[9px] font-bold leading-none text-ink-4 transition-colors hover:border-accent hover:text-accent focus:border-accent focus:text-accent focus:outline-none"
      >
        ?
      </button>
      {aberto &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={balaoRef}
            id={id}
            role="tooltip"
            data-testid="tooltip-balao"
            className="pointer-events-none fixed z-[1000] w-max max-w-[280px] rounded-lg px-3 py-2 text-left text-[13px] font-normal leading-snug text-white shadow-lg"
            style={{
              background: '#14171C',
              left: pos ? `${pos.left}px` : '-9999px',
              top: pos ? `${pos.top}px` : '-9999px',
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            {text}
          </div>,
          document.body,
        )}
    </span>
  );
}
